import { CSSObject } from "@emotion/react";
import { RequestModel } from "model";
import { DataItemView, IconName } from "swing-components";
import { translate } from "utils/string-table";

import { budgetNameWithPrefix } from "~components/FormFields";

/***** Styles *****/
const styles = {
  // Margin on the header elements helps alignment w/ Icon
  header: {
    margin: "2px 0",
  },
  content: {
    margin: 0,
    lineHeight: 1.5,
    fontSize: 14,
  },
} satisfies Record<string, CSSObject>;

type RequestDetailsCardProps = {
  request: RequestModel;
};

export function RequestDetailsCard(props: RequestDetailsCardProps) {
  const { request } = props;

  const { setting, supportLevel, toileting } = request.spedDetails;

  const showSpedDetails = setting?.length > 0 || supportLevel?.length > 0 || toileting;

  /***** Render *****/
  return (
    <>
      <div css={{ padding: "16px 0" }}>
        <h3 css={styles.header}>{translate("LABEL_DETAILS")}</h3>
      </div>
      <div className="swing-data-item-list">
        <DataItem
          icon="Grad Cap"
          label={translate("LABEL_GRADE")}
          content={request.getGradeDisplay()}
        />
        {showSpedDetails && (
          <DataItemView icon="Apple" label={<h4 css={styles.header}>{translate("LABEL_SPED")}</h4>}>
            <div css={styles.content}>
              {setting?.length > 0 && (
                <p css={{ margin: 0 }}>
                  {setting.map((item) => translate(`LABEL_SPED_SETTING_${item}`)).join(", ")}
                </p>
              )}
              {supportLevel?.length > 0 && (
                <p css={{ margin: 0 }}>
                  {supportLevel.map((item) => translate(`LABEL_SPED_SUPPORT_${item}`)).join(", ")}
                </p>
              )}
              {toileting && translate("LABEL_TOILETING_REQUIRED")}
            </div>
          </DataItemView>
        )}
        <DataItem
          icon="Book"
          label={translate("LABEL_SUBJECT")}
          content={request.getSubjectDisplay({ showUnknownAsNote: false })}
        />
        <DataItem
          icon="Website"
          label={translate("LABEL_BILINGUAL_REQUIREMENT")}
          content={request.getBilingualRequirementDisplay()}
        />
        <DataItem
          icon="Teacher"
          label={translate("LABEL_TEACHER")}
          content={request.getTeacherDisplay() || translate("NOT_PROVIDED")}
        />
        {request.position.id ? (
          <DataItem
            icon="Briefcase"
            label={translate("LABEL_POSITION")}
            content={request.position.displayName || translate("NOT_PROVIDED")}
          />
        ) : (
          <DataItem
            icon="Verified"
            label={translate("LABEL_CRED_PERMIT")}
            content={
              request.isCredentialRequired
                ? translate("CREDENTIALS_REQUIRED")
                : translate("CREDENTIALS_NOT_REQUIRED")
            }
          />
        )}
        <DataItemView
          icon="Phone"
          label={<h4 css={styles.header}>{translate("LABEL_CONTACT")}</h4>}
        >
          <div css={{ display: "flex", flexDirection: "column" }}>
            {request.contactName && <p css={styles.content}>{request.contactName}</p>}
            {request.contactPhone && <p css={styles.content}>{request.contactPhone}</p>}
            {request.contactEmail && <p css={styles.content}>{request.contactEmail}</p>}
          </div>
        </DataItemView>
        <DataItem
          icon="Clipboard"
          label={translate("LABEL_LESSON_PLAN")}
          content={
            request.isLessonPlanProvided
              ? translate("LESSON_PLAN_ON_ARRIVAL")
              : translate("NOT_PROVIDED")
          }
        />
        <DataItem icon="Note" label={translate("LABEL_NOTES")} content={request.notes} />
        {request.budget && (
          <DataItem
            icon="Monetization"
            label={translate("LABEL_BUDGET")}
            content={budgetNameWithPrefix({ budget: request.budget })}
          />
        )}
      </div>
    </>
  );
}

/***** Helper Components *****/
type DataItemProps = {
  icon: IconName;
  label: string;
  content: string;
};

function DataItem(props: DataItemProps) {
  const { icon, label, content } = props;
  return (
    <DataItemView icon={icon} label={<h4 css={styles.header}>{label}</h4>}>
      <p css={styles.content}>{content}</p>
    </DataItemView>
  );
}
